import React, { useState, useRef, useEffect } from 'react';
import './BatchScraper.css';

// Backend URL - für SSE direkt zum Backend (kein Proxy-Buffering)
// Im Entwicklungsmodus direkt zu localhost:5555, in Produktion relativ
const API_BASE = process.env.NODE_ENV === 'development' ? 'http://localhost:5555' : '';

function BatchScraper() {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ updated: 0, errors: 0 });
  const [lastCheckpoint, setLastCheckpoint] = useState(null);
  const abortControllerRef = useRef(null);

  // Filter-State
  const [manufacturers, setManufacturers] = useState([]);
  const [shops, setShops] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState('all');
  const [selectedShop, setSelectedShop] = useState('all');
  const [onlyOnlineProducts, setOnlyOnlineProducts] = useState(true); // Standardmäßig nur Online-Produkte

  // Lade Hersteller und Shops + Checkpoint beim Mount
  useEffect(() => {
    const loadFilters = async () => {
      try {
        // Lade Hersteller
        const manRes = await fetch(`${API_BASE}/api/manufacturers`);
        if (manRes.ok) {
          const manData = await manRes.json();
          setManufacturers(manData);
        }

        // Lade Shops
        const shopRes = await fetch(`${API_BASE}/api/stores`);
        if (shopRes.ok) {
          const shopData = await shopRes.json();
          setShops(shopData);
        }

        // 🔄 Lade letzten Checkpoint
        const token = localStorage.getItem('accessToken');
        const checkpointRes = await fetch(`${API_BASE}/api/batch-scraper/checkpoint`, {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
          }
        });
        if (checkpointRes.ok) {
          const checkpointData = await checkpointRes.json();
          if (checkpointData.success && checkpointData.checkpoint) {
            setLastCheckpoint(checkpointData.checkpoint);
            console.log('Checkpoint gefunden:', checkpointData.checkpoint);
          }
        }
      } catch (err) {
        console.error('Fehler beim Laden der Filter/Checkpoint:', err);
      }
    };
    loadFilters();
  }, []);

  const startScraping = async (fromCheckpoint = false) => {
    setIsRunning(true);
    setIsPaused(false);
    
    // Wenn nicht von Checkpoint, lösche Stats
    if (!fromCheckpoint) {
      setLogs([]);
      setStats({ updated: 0, errors: 0 });
    }
    
    // Nachricht für Neustart mit Filter-Info
    let filterInfo = fromCheckpoint ? '🔄 Scraping von Checkpoint wiederaufgenommen' : '🚀 Scraping neu gestartet';
    if (onlyOnlineProducts) {
      filterInfo += ' | Nur Online-Produkte';
    } else {
      filterInfo += ' | Alle Produkte';
    }
    if (selectedManufacturer !== 'all') {
      const mfr = manufacturers.find(m => m._id === selectedManufacturer);
      filterInfo += ` | Hersteller: ${mfr?.name || 'unbekannt'}`;
    }
    if (selectedShop !== 'all') {
      const shop = shops.find(s => s._id === selectedShop);
      filterInfo += ` | Shop: ${shop?.name || 'unbekannt'}`;
    }
    if (!fromCheckpoint) {
      setLogs([{ type: 'info', text: filterInfo }]);
    } else {
      setLogs(prev => [...prev, { type: 'info', text: filterInfo }]);
    }

    abortControllerRef.current = new AbortController();

    try {
      // Direkt zum Backend für SSE-Support mit Filter-Parametern
      let urlPath = `${API_BASE}/api/batch-scraper/scrape-all-prices`;
      const params = new URLSearchParams();
      
      // Wenn von Checkpoint: verwende die gespeicherten Daten
      if (fromCheckpoint && lastCheckpoint) {
        params.append('resumeFromProductId', lastCheckpoint.productId);
        params.append('resumeFromShopIndex', lastCheckpoint.shopIndex);
      }
      
      // Nur Online-Produkte Parameter
      if (!onlyOnlineProducts) {
        params.append('includeOffline', 'true');
      }
      
      if (selectedManufacturer !== 'all') {
        params.append('manufacturerId', selectedManufacturer);
      }
      if (selectedShop !== 'all') {
        params.append('shopId', selectedShop);
      }

      const fullUrl = params.toString() ? `${urlPath}?${params.toString()}` : urlPath;
      
      // Auth-Token aus localStorage holen
      const token = localStorage.getItem('accessToken');
      
      console.log('Starting fetch to', fullUrl);
      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Accept': 'text/event-stream',
          'Authorization': token ? `Bearer ${token}` : '',
        },
        signal: abortControllerRef.current.signal
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers.get('content-type'));

      if (!response.ok) {
        const errorMsg = `Fehler: Server antwortet mit Status ${response.status}`;
        console.error(errorMsg);
        setLogs(prev => [...prev, { type: 'error', text: errorMsg }]);
        setIsRunning(false);
        return;
      }

      if (!response.body) {
        console.error('Response body is null - ReadableStream not supported');
        setLogs(prev => [...prev, { type: 'error', text: 'Browser unterstützt ReadableStream nicht' }]);
        setIsRunning(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          console.log('Stream done');
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        // Keep the last incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const message = JSON.parse(line.slice(6));
              console.log('Received message:', message);
              setLogs(prev => [...prev, message]);

              // Handle checkpoint messages
              if (message.type === 'checkpoint') {
                setLastCheckpoint(message.data);
              }

              // Update stats
              if (message.type === 'success') {
                setStats(prev => ({ ...prev, updated: prev.updated + 1 }));
              } else if (message.type === 'error') {
                setStats(prev => ({ ...prev, errors: prev.errors + 1 }));
              }
            } catch (e) {
              console.warn('Parse error for line:', line, e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Fetch error:', err);
      if (err.name !== 'AbortError') {
        setLogs(prev => [...prev, { type: 'error', text: `Fehler: ${err.message}` }]);
      }
    } finally {
      setIsRunning(false);
    }
  };

  const pauseScraping = () => {
    // ⏸️ Pausiere nur die Client-Verbindung, Server läuft weiter
    setIsPaused(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setLogs(prev => [...prev, { type: 'warning', text: '⏸️ Scraping pausiert (Server läuft noch weiter)' }]);
  };

  const resumeScraping = () => {
    // 🔄 Setze isPaused zurück und starte von vorne
    setIsPaused(false);
    startScraping(false); // Starte normales Scraping (nicht von Checkpoint)
  };

  const resumeFromCheckpoint = () => {
    // 🔄 Starte von letztem Checkpoint
    if (!lastCheckpoint) {
      setLogs(prev => [...prev, { type: 'error', text: '❌ Kein Checkpoint vorhanden' }]);
      return;
    }
    setIsPaused(false);
    startScraping(true); // Starte mit Checkpoint-Resume
  };

  const stopScraping = async () => {
    // Log Nachricht für Stop
    setLogs(prev => [...prev, { type: 'error', text: '⛔ Scraping gestoppt' }]);
    setIsPaused(false);
    
    // Stop die SSE-Verbindung
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Benachrichtige den Server
    try {
      await fetch(`${API_BASE}/api/batch-scraper/stop`, {
        method: 'POST',
        headers: {
          'Authorization': localStorage.getItem('accessToken') ? `Bearer ${localStorage.getItem('accessToken')}` : '',
        }
      });
    } catch (err) {
      console.error('Fehler beim Stoppen:', err);
    }

    setIsRunning(false);
  };

  const clearCheckpoint = async () => {
    // 🗑️ Lösche den Checkpoint
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE}/api/batch-scraper/clear-checkpoint`, {
        method: 'POST',
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
        }
      });
      if (response.ok) {
        setLastCheckpoint(null);
        setLogs(prev => [...prev, { type: 'info', text: '✅ Checkpoint gelöscht' }]);
      }
    } catch (err) {
      console.error('Fehler beim Löschen des Checkpoints:', err);
    }
  };

  return (
    <div className="batch-scraper">
      <div className="scraper-header">
        <h2>🔄 Batch-Scraper</h2>
        <p>Alle Preise neu abfragen und aktualisieren</p>
      </div>

      {/* Filter Section */}
      <div className="scraper-filters">
        <div className="filter-group">
          <label htmlFor="manufacturer-filter">🏭 Hersteller:</label>
          <select 
            id="manufacturer-filter"
            value={selectedManufacturer}
            onChange={(e) => setSelectedManufacturer(e.target.value)}
            disabled={isRunning}
          >
            <option value="all">✓ Alle Hersteller</option>
            {manufacturers.map(mfr => (
              <option key={mfr._id} value={mfr._id}>
                {mfr.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="shop-filter">🛒 Shop:</label>
          <select 
            id="shop-filter"
            value={selectedShop}
            onChange={(e) => setSelectedShop(e.target.value)}
            disabled={isRunning}
          >
            <option value="all">✓ Alle Shops</option>
            {shops.map(shop => (
              <option key={shop._id} value={shop._id}>
                {shop.name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group checkbox-group">
          <label>
            <input 
              type="checkbox"
              checked={onlyOnlineProducts}
              onChange={(e) => setOnlyOnlineProducts(e.target.checked)}
              disabled={isRunning}
            />
            <span>✅ Nur Online-Produkte</span>
          </label>
        </div>
      </div>

      <div className="scraper-controls">
        <button
          className="btn-start-scraping"
          onClick={() => startScraping(false)}
          disabled={isRunning}
        >
          {isRunning ? '⏳ Scraping läuft...' : '🚀 Scraper Starten'}
        </button>
        <button
          className="btn-pause-scraping"
          onClick={pauseScraping}
          disabled={!isRunning || isPaused}
        >
          ⏸️ Scraper Pausieren
        </button>
        <button
          className="btn-resume-scraping"
          onClick={resumeScraping}
          disabled={!isPaused}
        >
          ▶️ Weiterführen
        </button>
        <button
          className="btn-stop-scraping"
          onClick={stopScraping}
          disabled={!isRunning && !isPaused}
        >
          ⏹️ Scraper Stoppen
        </button>
      </div>

      {/* Checkpoint Section */}
      {lastCheckpoint && (
        <div className="checkpoint-section">
          <div className="checkpoint-header">
            <h3>💾 Letzter Checkpoint</h3>
            <button 
              className="btn-clear-checkpoint"
              onClick={clearCheckpoint}
            >
              🗑️ Löschen
            </button>
          </div>
          <div className="checkpoint-info">
            <div className="checkpoint-detail">
              <span className="label">Produkt:</span>
              <span className="value">{lastCheckpoint.productName}</span>
              <span className="subtext">({lastCheckpoint.productIndex + 1}. Produkt)</span>
            </div>
            <div className="checkpoint-detail">
              <span className="label">Shop:</span>
              <span className="value">#{lastCheckpoint.shopIndex + 1}</span>
              {lastCheckpoint.shopName && (
                <span className="subtext">({lastCheckpoint.shopName})</span>
              )}
            </div>
            <div className="checkpoint-detail">
              <span className="label">Zeit:</span>
              <span className="value">{new Date(lastCheckpoint.timestamp).toLocaleTimeString('de-DE')}</span>
            </div>
            <button 
              className="btn-resume-from-checkpoint"
              onClick={resumeFromCheckpoint}
              disabled={isRunning}
            >
              🔄 Von hier aus fortsetzen
            </button>
          </div>
        </div>
      )}

      <div className="scraper-stats">
        <div className="stat">
          <span className="label">✅ Aktualisiert:</span>
          <span className="value">{stats.updated}</span>
        </div>
        <div className="stat">
          <span className="label">❌ Fehler:</span>
          <span className="value">{stats.errors}</span>
        </div>
      </div>

      <div className="console-container">
        <div className="console-header">
          📋 Konsole
        </div>
        <div className="console-output">
          {logs.length === 0 && !isRunning && (
            <div className="console-line placeholder">
              Klicke auf "Scraping starten" um zu beginnen...
            </div>
          )}
          {logs.map((log, idx) => (
            <div key={idx} className={`console-line ${log.type}`}>
              {log.text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default BatchScraper;
